// tslint:disable:no-submodule-imports
import { ApolloClient } from 'apollo-client';
import { flatten } from 'lodash';
import React, { FC, useContext } from 'react';
import { withApollo, WithApolloClient } from 'react-apollo';
import { useHistory } from 'react-router-dom';
import { ActionMeta, components, ValueType } from 'react-select';
import AsyncSelect from 'react-select/async';
import { ReactComponent as MagnifyingGlass } from 'src/assets/images/magnifying-glass.svg';
import { Regular } from 'src/styles/fonts';
import styled from 'styled-components';
import { ALL_REGIONS } from '../../../components/regions-menu/regions-menu';
import { IAddress, IShipTo } from '../../../graphql/types';
import { getUserSalesOrgs } from '../../../libs/auth';
import { RegionsContext } from '../../../regions-context';
import { Black, White } from '../../../styles/colours';
import { orderSimulationRoute } from '../../order-simulation/route';
import { GET_SHIP_TO_CUSTOMER_SEARCH } from '../queries/get-ship-to-customer-search';
import { Option } from './ship-to-option';

export interface ISelected {
  value: string;
  label: string;
}

interface IApolloSelectProps {
  client?: ApolloClient<any>;
  onSelect: (selected: ValueType<ISelected>, action: ActionMeta) => void;
  soldToId: string;
}

type ShipToDropDownProps = WithApolloClient<IApolloSelectProps>;

interface ICallback {
  value: string;
  shipToId: string;
  address: IAddress;
  label: string;
  type: 'Ship-To';
}

const DropdownIndicator = (props: any) => {
  return (
    <components.DropdownIndicator {...props}>
      <MagnifyingGlass />
    </components.DropdownIndicator>
  );
};

const StyledSelect = styled.div`
  font-family: ${Regular};
  background: ${White};
  color: ${Black};
  border: none;
  font-size: 1rem;
  margin-left: 1rem;
  margin-top: 0.2rem;
  width: 25rem;
  outline: none;
  ::placeholder {
    opacity: 0.6;
  }
`;

const ShipToDropDown: FC<ShipToDropDownProps> = ({
  client,
  onSelect,
  soldToId,
}) => {
  const orgs = getUserSalesOrgs();
  const { regions } = useContext(RegionsContext);
  let salesOrgs = flatten(
    regions
      .filter(region => ALL_REGIONS[region])
      .map(region => ALL_REGIONS[region].salesOrgs)
  );
  if (salesOrgs.length === 0) {
    salesOrgs = orgs;
  }
  const loadOptions = async (inputValue: any, callback: any) => {
    if (inputValue.length > 0) {
      const responses = await Promise.all([
        client.query({
          query: GET_SHIP_TO_CUSTOMER_SEARCH,
          variables: { name: inputValue, customerId: soldToId, salesOrgs },
        }),
      ]);

      const [getShipToResponse] = responses;

      let options: ICallback[] = [];

      if (getShipToResponse.data) {
        options = getShipToResponse.data.shipToSearch.map(
          (shipTo: IShipTo) => ({
            value: shipTo.id,
            label: shipTo.address.name,
            address: shipTo.address,
            type: 'Ship-To',
          })
        );
      }
      if (options.length > 0) {
        callback(options);
      }
    }
  };

  return (
    <StyledSelect data-type="search-dropdown">
      <AsyncSelect
        autoFocus={true}
        classNamePrefix="react-select"
        cacheOptions={false}
        loadOptions={loadOptions}
        defaultOptions={true}
        onChange={onSelect}
        components={{ Option, DropdownIndicator }}
        maxMenuHeight={600}
        placeholder="Search ship to name..."
        styles={{
          menuPortal: provided => ({
            ...provided,
            zIndex: 9999,
          }),
        }}
        menuPortalTarget={document.querySelector('body')}
      />
    </StyledSelect>
  );
};

const SearchDropDown = withApollo<IApolloSelectProps>(ShipToDropDown);

const ShipToSearchDropdown: FC<{
  soldToId: string;
  productId: string;
  quantity: string;
}> = ({ soldToId, productId, quantity }) => {
  const history = useHistory();
  const onSelect = (props: any) => {
    const { value } = props;
    history.push(
      orderSimulationRoute(soldToId, {
        shipToId: value,
        productId,
        quantity,
      })
    );
  };
  return <SearchDropDown soldToId={soldToId} onSelect={onSelect} />;
};

export { ShipToSearchDropdown };
